import { useState, useEffect, useRef } from "react";
import { AlertTriangle, X, ArrowRight, Bell, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import authService from "../../api/authService";
import dayjs from "dayjs";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

/**
 * Fetches professional profile licenses and renders a floating 
 * mobile-style push notification banner if any license or medical 
 * certificate is expired or expiring within 30 days.
 */
const ExpiredLicenseBanner = () => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const [dismissState, setDismissState] = useState(false);
    const bannerRef = useRef(null);

    const { data, isLoading } = useQuery({
        queryKey: ["professionalProfileLicenses"],
        queryFn: () => authService.getProfessionalProfile(),
        staleTime: 5 * 60 * 1000,
    });

    useGSAP(() => {
        if (bannerRef.current && !dismissState) {
            gsap.from(bannerRef.current, {
                y: -80,
                opacity: 0,
                scale: 0.9,
                rotationX: -15,
                duration: 1.2,
                ease: "elastic.out(1, 0.4)",
                delay: 0.3
            });
        }
    }, [dismissState]);

    if (isLoading || dismissState) return null;

    const profile = data?.data;
    const licenses = profile?.licenses || [];
    const today = dayjs();
    const warnThreshold = today.add(30, "day");

    const expiredLicenses = licenses.filter((l) => l.license_status === "expired");
    const expiringLicenses = licenses.filter((l) => {
        if (l.license_status === "expired") return false;
        if (!l.expiry_date) return false;
        const exp = dayjs(l.expiry_date);
        return exp.isValid() && exp.isBefore(warnThreshold);
    });

    const expiredMedical = licenses.filter((l) => l.medical_status === "expired");
    const expiringMedical = licenses.filter((l) => {
        if (l.medical_status === "expired") return false;
        if (!l.medical_expiry_date) return false;
        const exp = dayjs(l.medical_expiry_date);
        return exp.isValid() && exp.isBefore(warnThreshold);
    });

    const totalIssues = expiredLicenses.length + expiredMedical.length + expiringLicenses.length + expiringMedical.length;
    if (totalIssues === 0) return null;

    const handleDismiss = () => {
        setDismissState(true);
    };

    const combinedList = [
        ...expiredLicenses.map(l => ({ ...l, kind: "license", isExpired: true })),
        ...expiringLicenses.map(l => ({ ...l, kind: "license", isExpired: false })),
        ...expiredMedical.map(l => ({ ...l, kind: "medical", isExpired: true })),
        ...expiringMedical.map(l => ({ ...l, kind: "medical", isExpired: false })),
    ];

    return (
        <div ref={bannerRef} className="fixed top-24 right-4 md:right-8 z-[200] w-full max-w-[380px] perspective-1000">
            <div className="bg-white/95 backdrop-blur-2xl border border-white/50 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] rounded-[28px] overflow-hidden transition-all duration-300 ring-1 ring-black/5">
                
                {/* Notification Area (Clickable to expand) */}
                <div 
                    onClick={() => setExpanded(!expanded)}
                    className="flex justify-between items-start pt-4 px-4 pb-2 cursor-pointer hover:bg-white/50 transition-colors"
                >
                    <div className="flex gap-3 w-full">
                        {/* App Icon Bubble */}
                        <div className="mt-1 flex-shrink-0 w-11 h-11 rounded-[14px] bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                            <Bell size={20} className="text-white fill-white/20" />
                        </div>
                        
                        {/* Title & Preview Text */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-1 mb-1">
                                <p className="font-semibold text-gray-900 text-[15px] leading-tight pr-2">
                                    {expiredLicenses.length + expiredMedical.length > 0 ? "Action Required: Expired Licenses" : "Licenses Expiring Soon"}
                                </p>
                                <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                                    <span className="text-[11px] text-gray-400 font-medium">now</span>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                                        className="text-gray-400 hover:text-gray-600 p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                                        title="Dismiss"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-end justify-between gap-2">
                                <p className="text-[14px] text-gray-600 leading-snug tracking-tight">
                                    {expiredLicenses.length + expiredMedical.length > 0
                                        ? `You have ${expiredLicenses.length + expiredMedical.length} expired license(s).`
                                        : `${expiringLicenses.length + expiringMedical.length} license(s) will expire soon.`}
                                    {" "}Tap to {expanded ? "hide" : "view"} details.
                                </p>
                                <div className="flex-shrink-0 text-gray-400 mb-0.5 ml-1">
                                    <ChevronDown size={18} className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Expanded Details - Accordion reveal */}
                <div 
                    className={`transition-all duration-300 ease-in-out overflow-hidden bg-gray-50/50 ${expanded ? 'max-h-[500px] opacity-100 p-4 border-t border-gray-100/50' : 'max-h-0 opacity-0 p-0 border-t-0'}`}
                >
                    <div className="bg-white/90 rounded-2xl shadow-sm border border-gray-100 p-3 mb-4 space-y-3 max-h-[250px] overflow-y-auto">
                        {combinedList.map((l, i) => {
                            const expiryDate = l.kind === "medical" ? l.medical_expiry_date : l.expiry_date;
                            const label = l.kind === "medical" ? `Medical Certificate` : l.license_type?.split(",")[0]?.toUpperCase() || "License";
                            return (
                                <div key={i} className="flex justify-between items-center gap-2 border-b border-gray-50 last:border-0 pb-2 last:pb-0">
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-sm font-semibold text-gray-800 truncate">{label}</span>
                                        <span className="text-xs text-gray-500">
                                            {l.isExpired ? "Expired" : "Expires"} {dayjs(expiryDate).format("DD MMM YYYY")}
                                        </span>
                                    </div>
                                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${l.isExpired ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                                        {l.isExpired ? "EXPIRED" : "SOON"}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    
                    <div className="flex gap-2.5">
                        <button 
                            onClick={(e) => { e.stopPropagation(); setExpanded(false); handleDismiss(); }}
                            className="flex-1 py-3 px-4 rounded-xl text-gray-700 font-semibold text-sm bg-gray-200/50 hover:bg-gray-200/80 transition-all active:scale-95"
                        >
                            Dismiss
                        </button>
                        <button 
                            onClick={(e) => { e.stopPropagation(); navigate("/manage-profile"); }}
                            className="flex-[1.5] flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-white font-semibold text-sm bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all active:scale-95"
                        >
                            Update Now <ArrowRight size={14} />
                        </button>
                    </div>
                </div>

                {/* iPhone visual drag handle at bottom when closed */}
                {!expanded && (
                    <div className="w-full flex justify-center pb-2.5 pt-1 cursor-pointer" onClick={() => setExpanded(true)}>
                        <div className="w-10 h-1 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors"></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExpiredLicenseBanner;
